<script setup>
import { computed, nextTick, ref, shallowRef, watch } from 'vue';
import moment from 'moment';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiAvatar from '@/components/ui/UiAvatar.vue';
import UiBadgeList from '@/components/ui/UiBadgeList.vue';
import UiDetailList from '@/components/ui/UiDetailList.vue';
import UiChoiceCards from '@/components/ui/UiChoiceCards.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiFormFields from '@/components/ui/UiFormFields.vue';
import UiOptionList from '@/components/ui/UiOptionList.vue';
import UiPerson from '@/components/ui/UiPerson.vue';
import UiSteps from '@/components/ui/UiSteps.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import StudentDisciplinaryDialog from '@/components/Dialogs/Forms/StudentDisciplinaryDialog.vue';
import { apiRequest, errorMessage } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useConfirm } from '@/composables/useConfirm';
import { photoUrl } from '@/composables/useProfileSettings';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { useToast } from '@/composables/useToast';
import { FACULTY_ROLE_CODES, PREFECT_ROLE_CODES, RoleCode } from '@/constants/accounts';
import {
  ATTACHMENT_ACCEPT,
  COMPLIANCE_STATUSES,
  REPORT_SEVERITIES,
  attachmentProblem,
  attachmentRule,
  caseActionDetails,
  complianceStatus,
  fileOf,
  reportDetails,
  reportSeverity,
  reportType,
} from '@/constants/reports';
import { formatDate, formatDateTime, formatTime, titleCase } from '@/utils/format';

useSessionGuard();

// Discipline records. A report is first "for resolution" with the student's
// class adviser, who resolves it or, when it needs further intervention,
// endorses it to the Prefect of Discipline with the reason. The subject
// teacher who filed it only follows it here. The Prefect may also record a
// case no teacher reported (Add record); it starts with them and the class
// adviser is told. On their cases the Prefect assigns a disciplinary action
// and follows the student's compliance (status, due date, remarks), resolves
// the case (and may tell the parents), or schedules a conference with the
// parents and student, a meeting with the parents (who can accept, decline
// or move it), or a counseling session with the students. The class
// advisers are told either way.
const store = useStore();
const router = useRouter();
const toast = useToast();
const { confirm } = useConfirm();
const roleCode = store.getters.activeRoleCode;
const isTeacher = FACULTY_ROLE_CODES.includes(roleCode);
const isPrefect = PREFECT_ROLE_CODES.includes(roleCode);
const isAdviser = roleCode === RoleCode.Adviser;

const TABS = isTeacher
  ? [
      { id: 1, name: 'For resolution' },
      { id: 2, name: 'With the Prefect' },
      { id: 3, name: 'Resolved' },
    ]
  : [
      { id: 1, name: 'For resolution' },
      { id: 4, name: 'Meetings' },
      { id: 3, name: 'Resolved' },
    ];
const tab = shallowRef(TABS[0].id);
const year = computed(() => store.getters.getFilterSelected);

const { items, loading, error, load } = useListResource(async () => {
  if (!isTeacher && !isPrefect) return [];
  const rows = isTeacher
    ? await apiRequest('get', `/parent-records/getDisciplinaryReport/${year.value}/${tab.value}/0`)
    : await apiRequest('get', `/parent-records/getPrefectReport/${year.value}/${tab.value}`);
  return (Array.isArray(rows) ? rows : []).map((r) => ({ ...r, name: titleCase(r.name) }));
});
watch([tab, year], load, { immediate: true });

// Where a report is, in words.
const STAGES = {
  0: { text: 'For resolution', variant: 'amber' },
  1: { text: 'With the Prefect', variant: 'violet' },
  2: { text: 'With the Prefect', variant: 'violet' },
  5: { text: 'Parent meeting', variant: 'blue' },
  3: { text: 'Resolved', variant: 'green' },
  4: { text: 'Resolved by the Prefect', variant: 'green' },
};
// What the prefect can schedule. Meetings from before there was a choice
// have no type: those were parent meetings.
const MEETING_TYPES = [
  {
    id: 'parent_student',
    name: 'Parents and student',
    stage: 'Parent & student conference',
    description: 'The parents come in with their child. Notifies the parents and the adviser.',
  },
  {
    id: 'parent',
    name: 'Parents meeting',
    stage: 'Parent meeting',
    description: 'The parents only. Notifies the parents and the adviser.',
  },
  {
    id: 'counseling',
    name: 'Counseling (students)',
    stage: 'Counseling',
    description: 'A session with the students on the report. Notifies the adviser.',
  },
];
const meetingTypeOf = (r) => MEETING_TYPES.find((t) => t.id === r?.meeting_type) ?? MEETING_TYPES[1];
const isCounseling = (r) => r?.meeting_type === 'counseling';
const stageOf = (r) =>
  Number(r.status) === 5 ? { ...STAGES[5], text: meetingTypeOf(r).stage } : (STAGES[Number(r.status)] ?? STAGES[0]);
// When it is: the parent's answer may have moved a meeting; counseling
// keeps the time the prefect set.
const meetingWhen = (r) =>
  r.meeting_schedule
    ? formatDateTime(r.meeting_schedule)
    : r.meeting_date
      ? `${formatDate(r.meeting_date)} ${formatTime(r.meeting_time)}`
      : '';
const MEETING = {
  pending: { text: 'Waiting for the parent', variant: 'amber' },
  accepted: { text: 'Parent confirmed', variant: 'green' },
  declined: { text: 'Parent declined', variant: 'red' },
  cancelled: { text: 'Cancelled', variant: 'secondary' },
};
const meetingOf = (r) => (r.meeting_status ? MEETING[r.meeting_status] : null);

const headers = computed(() => [
  { title: 'Student', key: 'name' },
  { title: 'Reported by', key: 'teacher_name' },
  { title: 'Grade', key: 'grade_level' },
  { title: 'Type', key: 'report_type' },
  { title: 'Filed', key: 'created_at' },
  ...(tab.value === 4 ? [{ title: 'Meeting', key: 'meeting_schedule' }] : [{ title: 'Stage', key: 'status' }]),
  ...(isPrefect ? [{ title: 'Severity', key: 'severity' }, { title: 'Compliance', key: 'compliance' }] : []),
]);

// --- Add record (Prefect) -------------------------------------------------
// The Prefect records a case no teacher reported; once saved it opens, so
// they can go on to the action and a conference or counseling session.
const recordOpen = shallowRef(false);
async function recorded(id) {
  if (tab.value !== 1) {
    tab.value = 1;
    // Let the tab's own reload start first, so this one is the latest.
    await nextTick();
  }
  await load();
  const row = items.value.find((r) => Number(r.reportID) === Number(id));
  if (row) view(row);
}

// --- Disciplinary action and compliance (Prefect) ---------------------------
// The actions the Prefect manages on the Disciplinary Actions page. An
// archived one is still shown on a case that already has it.
const actions = shallowRef([]);
async function loadActions() {
  if (!isPrefect) return;
  const rows = await apiRequest('get', '/parent-records/disciplinary-actions', undefined, {
    params: { includeArchived: true },
  }).catch(() => []);
  actions.value = Array.isArray(rows) ? rows : [];
}
loadActions();
const actionChoices = computed(() =>
  actions.value.filter((a) => !a.archived || Number(a.id) === Number(viewing.value?.disciplinary_action_id)),
);
// Every case that reached the Prefect, open or resolved: they keep
// following the student's compliance after resolving it.
const canSetAction = computed(() => isPrefect && [1, 2, 4, 5].includes(status.value));
const isDisciplinary = computed(() => Number(viewing.value?.report_type) === 2);
const actionForm = ref({});
const actionDirty = computed(() => {
  const r = viewing.value;
  if (!r) return false;
  const f = actionForm.value;
  return (
    Number(f.disciplinary_action_id || 0) !== Number(r.disciplinary_action_id || 0) ||
    (f.compliance_status || null) !== (r.compliance_status || null) ||
    (f.compliance_due_date || null) !== (r.compliance_due_date || null) ||
    String(f.action_taken ?? '').trim() !== String(r.action_taken ?? '')
  );
});
const complianceField = computed(() => [
  {
    key: 'compliance_status',
    label: 'Compliance status',
    type: 'select',
    items: COMPLIANCE_STATUSES,
  },
  { key: 'compliance_due_date', label: 'Due date', type: 'date' },
  {
    key: 'action_taken',
    label: 'Remarks / action taken',
    type: 'textarea',
    autocomplete: 'off',
    span: 2,
    placeholder: 'e.g. Submit a written undertaking signed by the parent/guardian.',
    rules: [(v) => String(v ?? '').length <= 2000 || 'Keep it under 2,000 characters.'],
  },
]);
// Overdue is worked out from the saved due date, so it shows what the
// server says, not the unsaved form.
const savedCompliance = computed(() => complianceStatus(viewing.value?.compliance));
const savingAction = shallowRef(false);
async function saveAction({ quiet = false } = {}) {
  const r = viewing.value;
  const f = actionForm.value;
  if (isDisciplinary.value && !f.disciplinary_action_id) {
    toast.error('Choose the disciplinary action.');
    return false;
  }
  savingAction.value = true;
  const body = {
    disciplinaryActionId: f.disciplinary_action_id || null,
    complianceStatus: f.compliance_status || null,
    dueDate: f.compliance_due_date || null,
    actionTaken: String(f.action_taken ?? '').trim() || null,
  };
  const ok = await run(() => apiRequest('patch', `/parent-records/reports/${r.reportID}/action`, body), {
    success: quiet ? '' : 'Disciplinary action saved.',
    error: 'Could not save the disciplinary action.',
  });
  savingAction.value = false;
  if (ok) {
    await load();
    const fresh = items.value.find((row) => Number(row.reportID) === Number(r.reportID));
    // Saved: the case now shows what the server has, overdue included.
    if (fresh) {
      viewing.value = fresh;
      resetActionForm(fresh);
    }
    // Saved from its own task: back to the case.
    if (!quiet) mode.value = null;
  }
  return ok;
}
function resetActionForm(r) {
  actionForm.value = {
    disciplinary_action_id: r?.disciplinary_action_id ? Number(r.disciplinary_action_id) : null,
    compliance_status: r?.compliance_status ?? null,
    compliance_due_date: r?.compliance_due_date ?? null,
    action_taken: r?.action_taken ?? '',
  };
}

// --- Review ---------------------------------------------------------------
const viewing = shallowRef(null);
const viewOpen = shallowRef(false);
const form = ref({});
const notifyParents = shallowRef(true);
// The dialog first shows the case and "What would you like to do?"; picking
// a task (resolve, endorse, action, meeting, edit) shows only that task's
// form, with Back to return. One thing at a time, for staff who aren't at
// ease with busy screens.
const mode = shallowRef(null);
const MODE_TITLES = {
  resolve: 'Resolve the case',
  endorse: 'Endorse to the Prefect of Discipline',
  action: 'Disciplinary action',
  meeting: 'Schedule a conference or counseling',
  edit: 'Edit the resolution note',
};
const MODE_HINTS = {
  resolve: 'Write what was done to settle it. It is saved on the case.',
  endorse: 'Say why it needs the Prefect of Discipline. They will see your reason.',
  action: 'Choose the action, then keep the compliance status up to date until the student completes it.',
  meeting: 'Choose who to call in, then the date and time.',
  edit: 'Correct your note or its attachment.',
};

const status = computed(() => Number(viewing.value?.status));
// The class adviser acts while it's with them (the server marks those rows);
// the prefect once escalated. Anyone else only views.
const canActAsTeacher = computed(() => isTeacher && status.value === 0 && !!viewing.value?.canHandle);
const canActAsPrefect = computed(() => isPrefect && [1, 2, 5].includes(status.value));
// A teacher who can't act on a report still waiting on its adviser would
// otherwise see a dialog with only Close, so say who it is waiting on.
const waitingNote = computed(() => {
  const r = viewing.value;
  if (!isTeacher || status.value !== 0 || canActAsTeacher.value || !r) return '';
  if (r.ownAdvisory) return 'You are this student’s class adviser. Switch to your Adviser role to resolve it or endorse it to the Prefect of Discipline.';
  if (!r.adviserID) {
    return `${r.room_section || 'This section'} has no class adviser yet, so no one can resolve this report. Ask an administrator to assign one in Rooms.`;
  }
  return `Waiting on the class adviser, ${titleCase(r.adviser_name)}, to resolve it or endorse it to the Prefect of Discipline. You can follow it here.`;
});
// Once resolved, whoever resolved it can still correct the record: the
// adviser their note, the prefect their note and attachment (the action
// stays in its own section).
const canEditNotes = computed(
  () => (isTeacher && status.value === 3 && !!viewing.value?.canEditNotes) || (isPrefect && status.value === 4),
);
const editing = computed(() => mode.value === 'edit');

const noteFields = computed(() => [
  {
    key: 'comments',
    label: mode.value === 'endorse' ? 'Why does it need the Prefect of Discipline?' : 'How was it resolved?',
    type: 'textarea',
    // The browser mustn't refill it with an earlier note.
    autocomplete: 'off',
    required: true,
    description: editing.value
      ? 'Saving changes the note others see on this report. The parents are not notified again.'
      : mode.value === 'endorse'
        ? 'e.g. The student keeps doing it after two talks; the parents need to be called in.'
        : canActAsPrefect.value
          ? 'Shared with the parents if you notify them.'
          : 'What was done, e.g. talked with the student and the parents; the student apologized.',
  },
  // Whoever resolves it, adviser or Prefect, may attach a file with the
  // resolution. It stays with the resolution, so endorsing doesn't take one.
  {
    key: 'file',
    hidden: () => mode.value === 'endorse',
    label: 'Attachment',
    type: 'file',
    accept: ATTACHMENT_ACCEPT,
    rules: [attachmentRule],
    description:
      editing.value && viewing.value?.resolution_file_name
        ? 'Optional. Choose a file to replace the current one. A PDF, JPG or PNG of up to 1MB.'
        : canActAsTeacher.value
          ? 'Optional, kept with the resolution (not sent when endorsing). A PDF, JPG or PNG of up to 1MB.'
          : 'Optional. A PDF, JPG or PNG of up to 1MB, e.g. a signed agreement.',
  },
]);
// The optional resolution file.
const chosenFile = () => fileOf(form.value.file);
// A note goes with every resolve or endorsement.
const hasNote = () => !!String(form.value.comments ?? '').trim();
const today = () => moment().format('YYYY-MM-DD');
const meetingType = shallowRef('parent_student');
const counseling = computed(() => meetingType.value === 'counseling');
const meetingFields = computed(() => [
  { key: 'date', label: 'Date', type: 'date', required: true, min: today() },
  { key: 'time', label: 'Time', type: 'time', required: true },
  // How the parents hear about it; counseling asks no parent.
  ...(counseling.value
    ? []
    : [
        {
          key: 'channel',
          label: 'Send through',
          type: 'select',
          required: true,
          span: 2,
          items: [
            { title: 'In-app notification and email', value: 'both' },
            { title: 'In-app notification only', value: 'app' },
            { title: 'Email only', value: 'email' },
          ],
          description: "Email goes to the parent's account email. Parents answer the request in the app either way.",
        },
      ]),
  counseling.value
    ? { key: 'note', label: 'Notes', type: 'textarea', span: 2, description: 'Shown on the report to the adviser and the reporting teacher.' }
    : { key: 'note', label: 'Message to the parents', type: 'textarea', span: 2, description: 'Sent to every parent asked. Edit it as needed.' },
]);
// Starting text for the message. Parents already see which child it's about
// and the date and time, so it covers only why and what to bring.
const NOTE_TEMPLATES = {
  parent_student:
    "Good day! We'd like to meet with you and your child at the Prefect of Discipline's office to talk about the report and how we can support your child together. Please bring a valid ID. If you can't make it at this time, you can suggest another time.",
  parent:
    "Good day! We'd like to meet with you at the Prefect of Discipline's office to talk about the report and how we can support your child together. Please bring a valid ID. If you can't make it at this time, you can suggest another time.",
  counseling: 'Counseling session at the Prefect of Discipline\'s office about the report.',
};
const noteTemplate = computed(() => NOTE_TEMPLATES[meetingType.value]);
// Switching the type swaps the starting text, unless the prefect wrote their own.
watch(meetingType, (next, prev) => {
  if (!form.value.note || form.value.note === NOTE_TEMPLATES[prev]) form.value = { ...form.value, note: NOTE_TEMPLATES[next] };
});

// Who a meeting request reaches: each student on the report (reported
// first, then those involved) with their linked parents, all ticked by
// default. Loaded when the prefect opens the meeting form.
const invitees = shallowRef([]);
const invitedIds = ref(new Set());
const loadingInvitees = shallowRef(false);
async function openMeeting() {
  mode.value = 'meeting';
  if (!form.value.note) form.value = { ...form.value, note: noteTemplate.value };
  loadingInvitees.value = true;
  try {
    const rows = await apiRequest('get', `/parent-records/reports/${viewing.value.reportID}/parents`);
    invitees.value = Array.isArray(rows) ? rows : [];
    invitedIds.value = new Set(invitees.value.flatMap((s) => s.parents.map((p) => p.id)));
    counseledIds.value = new Set(invitees.value.map((s) => s.studentID));
  } catch {
    invitees.value = [];
    toast.error('Could not load the parents.');
  } finally {
    loadingInvitees.value = false;
  }
}
function toggleInvite(id) {
  const next = new Set(invitedIds.value);
  next.has(id) ? next.delete(id) : next.add(id);
  invitedIds.value = next;
}
// For counseling: the students called in, all of them by default.
const counseledIds = ref(new Set());
function toggleCounseled(id) {
  const next = new Set(counseledIds.value);
  next.has(id) ? next.delete(id) : next.add(id);
  counseledIds.value = next;
}
// One request per parent per child, so a parent of two students gets two.
const inviteCount = computed(() =>
  invitees.value.reduce((n, s) => n + s.parents.filter((p) => invitedIds.value.has(p.id)).length, 0),
);
const parentPhoto = (p) => (p.photo && p.photo !== 'img_avatar.png' ? photoUrl(p.photo) : '');
// Mobile, phone and email, whichever the parent has filled in.
const parentContacts = (p) =>
  [
    p.mobile && { icon: 'mdi-cellphone', text: p.mobile },
    p.phone && p.phone !== p.mobile && { icon: 'mdi-phone-outline', text: p.phone },
    p.email && { icon: 'mdi-email-outline', text: p.email },
  ].filter(Boolean);
const chosenFor = (s) => s.parents.filter((p) => invitedIds.value.has(p.id)).length;

// A counseling session not yet held can be called off from the report too;
// the report goes back to For resolution and the advisers are told.
const canCancelCounseling = computed(() => {
  const r = viewing.value;
  if (!r || !canActAsPrefect.value || status.value !== 5 || !isCounseling(r) || r.meeting_status === 'cancelled') return false;
  return moment(`${r.meeting_date} ${String(r.meeting_time).slice(0, 5)}`, 'YYYY-MM-DD HH:mm').isAfter(moment());
});
async function cancelCounseling() {
  const r = viewing.value;
  const ok = await confirm({
    title: 'Cancel this counseling session?',
    description: `${meetingWhen(r)}. The advisers will be told, and the report goes back to For resolution.`,
    confirmText: 'Cancel counseling',
    cancelText: 'Keep it',
    destructive: true,
  });
  if (ok) act({ method: 'patch', action: 'counseling/cancel' }, undefined, 'Counseling cancelled. The adviser has been told.');
}

// --- Severity --------------------------------------------------------------
// The Prefect classifies every report that reaches them, at any stage; it
// feeds the behavior summary on their dashboard. Others only see it.
const savingSeverity = shallowRef(false);
async function setSeverity(value) {
  const r = viewing.value;
  const next = value ?? null;
  if (!r || next === (r.severity ?? null)) return;
  savingSeverity.value = true;
  const ok = await run(() => apiRequest('patch', `/parent-records/reports/${r.reportID}/severity`, { severity: next }), {
    success: next ? `Marked as ${reportSeverity(next).name.toLowerCase()}.` : 'Severity cleared.',
    error: 'Could not save the severity.',
  });
  savingSeverity.value = false;
  if (ok) {
    viewing.value = { ...r, severity: next };
    load();
  }
}

function view(report) {
  viewing.value = report;
  form.value = { comments: '', date: '', time: '', note: '', channel: 'both', file: null };
  resetActionForm(report);
  notifyParents.value = true;
  mode.value = null;
  removeAttachment.value = false;
  meetingType.value = 'parent_student';
  viewOpen.value = true;
}

const details = computed(() => {
  const r = viewing.value;
  if (!r) return [];
  return [
    { label: 'LRN', value: r.lrnNo },
    { label: 'Section', value: r.room_section },
    ...reportDetails(r),
  ];
});
// What others see of the Prefect's action; the Prefect edits it instead.
const actionDetails = computed(() => caseActionDetails(viewing.value));

// Where the case is, as steps: a teacher's report goes to the class
// adviser, then (if endorsed) the Prefect; a case the Prefect recorded
// starts with them.
const progress = computed(() => {
  const r = viewing.value;
  if (!r) return { steps: [], current: 0 };
  const st = Number(r.status);
  if (r.source === 'prefect') return { steps: ['Recorded by the Prefect', 'Being handled', 'Resolved'], current: st === 4 ? 2 : 1 };
  if (st === 3) return { steps: ['Reported', 'Class adviser', 'Resolved'], current: 2 };
  return {
    steps: ['Reported', 'Class adviser', 'Prefect of Discipline', 'Resolved'],
    current: st === 0 ? 1 : st === 4 ? 3 : 2,
  };
});

// One plain sentence on what happens next, and who does it.
const nextStep = computed(() => {
  const r = viewing.value;
  if (!r) return '';
  if (waitingNote.value) return waitingNote.value;
  const st = status.value;
  if (st === 0) {
    return canActAsTeacher.value
      ? 'This report is waiting for you. Resolve it, or endorse it to the Prefect of Discipline if it needs further intervention.'
      : 'Waiting for the class adviser.';
  }
  if (st === 1 || st === 2) {
    return isPrefect
      ? 'This case is with you. Set the disciplinary action, schedule a conference or counseling if needed, then resolve it.'
      : 'The Prefect of Discipline is handling this case.';
  }
  if (st === 5) {
    const what = meetingTypeOf(r).stage.toLowerCase();
    return isPrefect
      ? `A ${what} is set for ${meetingWhen(r)}. Resolve the case once it's done.`
      : `The Prefect of Discipline scheduled a ${what} for ${meetingWhen(r)}.`;
  }
  if (st === 3) return 'Resolved by the class adviser.';
  return isPrefect ? "Resolved. You can still update the student's compliance." : 'Resolved by the Prefect of Discipline.';
});

// "What would you like to do?": only what this person can do now.
const choices = computed(() => {
  const r = viewing.value;
  if (!r) return [];
  const list = [];
  if (canActAsTeacher.value) {
    list.push(
      { id: 'resolve', icon: 'mdi-check-circle-outline', title: 'Resolve it', description: 'You settled it with the student. Write what was done.' },
      {
        id: 'endorse',
        icon: 'mdi-arrow-up-bold-circle-outline',
        title: 'Endorse to the Prefect of Discipline',
        description: 'It needs further intervention. Say why.',
      },
    );
  }
  if (canSetAction.value) {
    list.push({
      id: 'action',
      icon: 'mdi-gavel',
      title: status.value === 4 ? 'Update the compliance' : 'Set the disciplinary action',
      description: 'The action, compliance status, due date and remarks.',
    });
  }
  if (canActAsPrefect.value) {
    list.push({
      id: 'meeting',
      icon: 'mdi-calendar-account-outline',
      title: meetingWhen(r) ? 'Schedule a new conference or counseling' : 'Schedule a conference or counseling',
      description: 'Call in the parents, the student, or both.',
    });
    if (canCancelCounseling.value) {
      list.push({ id: 'cancel-counseling', icon: 'mdi-calendar-remove-outline', title: 'Cancel the counseling session', description: meetingWhen(r) });
    }
    list.push({
      id: 'resolve',
      icon: 'mdi-check-circle-outline',
      title: 'Resolve the case',
      description: 'Write how it was resolved and, if you like, tell the parents.',
    });
  }
  if (canEditNotes.value) {
    list.push({ id: 'edit', icon: 'mdi-pencil-outline', title: 'Edit the resolution note', description: 'Correct the note or its attachment.' });
  }
  return list;
});
function choose(choice) {
  if (choice.id === 'cancel-counseling') return cancelCounseling();
  if (choice.id === 'meeting') return openMeeting();
  if (choice.id === 'edit') return startEdit();
  form.value = { ...form.value, comments: '', file: null };
  mode.value = choice.id;
}
// Back to the case, dropping the unsaved action changes.
function backToCase() {
  if (mode.value === 'action') resetActionForm(viewing.value);
  mode.value = null;
}
// Each note under who wrote it, in its own section. The adviser's reason
// for endorsing it stays in view while the Prefect writes their own
// resolution, which starts empty.
const noteDetails = computed(() => {
  const r = viewing.value;
  if (!r) return [];
  // "Resolution · Juan Dela Cruz, Adviser"; just the role when the writer
  // isn't known (notes from before writers were recorded).
  const by = (name, role) => (name ? `${titleCase(name)}, ${role}` : role);
  return [
    ...(r.endorsement_reason
      ? [{ label: `Reason for endorsement · ${by(r.adviser_notes_by_name, 'Adviser')}`, value: r.endorsement_reason, span: 2 }]
      : []),
    // Endorsements from before the reason had its own field kept it here.
    ...(r.adviser_notes && r.adviser_notes !== r.endorsement_reason
      ? [{ label: `Resolution · ${by(r.adviser_notes_by_name, 'Adviser')}`, value: r.adviser_notes, span: 2 }]
      : []),
    ...(r.prefect_notes
      ? [{ label: `Resolution · ${by(r.prefect_notes_by_name, 'Prefect of Discipline')}`, value: r.prefect_notes, span: 2 }]
      : []),
  ];
});
// The students in a counseling session, by name: the reported student or
// those tagged on the report.
function counseledNames(r) {
  let ids = [];
  try {
    ids = JSON.parse(r.meeting_students || '[]').map(Number);
  } catch {
    ids = [];
  }
  return ids
    .map((id) => (id === Number(r.id) ? r.name : (r.tagged || []).find((t) => Number(t.id) === id)?.name))
    .filter(Boolean)
    .join(', ');
}
const meetingDetails = computed(() => {
  const r = viewing.value;
  if (!r || !meetingWhen(r)) return [];
  return [
    { label: 'Type', value: meetingTypeOf(r).name },
    { label: 'When', value: meetingWhen(r) },
    isCounseling(r)
      ? { label: 'Students', value: counseledNames(r) || '—' }
      : { label: 'Parent', value: meetingOf(r)?.text ?? '—' },
    ...(r.meeting_notes ? [{ label: isCounseling(r) ? 'Notes' : 'Message', value: r.meeting_notes, span: 2 }] : []),
  ];
});

const { busy, run } = useAsyncAction();
async function act(path, body, success) {
  const ok = await run(() => apiRequest(path.method, `/parent-records/reports/${viewing.value.reportID}/${path.action}`, body), {
    success,
    error: 'Could not update the report.',
  });
  if (ok) {
    viewOpen.value = false;
    load();
  }
}

async function resolve() {
  // A disciplinary concern is resolved with its disciplinary action, saved
  // first along with any other change to the action section.
  if (canActAsPrefect.value && isDisciplinary.value && !actionForm.value.disciplinary_action_id) {
    toast.error('Choose the disciplinary action.');
    return;
  }
  if (!hasNote()) {
    toast.error('Write how it was resolved first.');
    return;
  }
  const file = chosenFile();
  const problem = attachmentProblem(file);
  if (problem) {
    toast.error(problem);
    return;
  }
  if (canActAsPrefect.value && actionDirty.value && !(await saveAction({ quiet: true }))) return;
  const fields = {
    comments: form.value.comments,
    notifyParents: canActAsPrefect.value && notifyParents.value,
  };
  // With a file it goes as a multipart form, so the switch travels as text.
  let body = fields;
  if (file) {
    body = new FormData();
    body.append('comments', fields.comments);
    body.append('notifyParents', String(fields.notifyParents));
    body.append('file', file);
  }
  act(
    { method: 'patch', action: 'resolve' },
    body,
    canActAsPrefect.value && notifyParents.value ? 'Resolved. The parents have been told.' : 'Report resolved.',
  );
}
// The resolution attachment and the evidence filed with the report come
// with the session token, so they are fetched and saved from here rather
// than linked to.
const downloading = shallowRef(null);
async function downloadAttachment(kind = 'resolution') {
  const evidence = kind === 'evidence';
  downloading.value = kind;
  try {
    const blob = await apiRequest(
      'get',
      `/parent-records/reports/${viewing.value.reportID}/attachment${evidence ? '?kind=evidence' : ''}`,
      undefined,
      { responseType: 'blob' },
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = (evidence ? viewing.value.evidence_file_name : viewing.value.resolution_file_name) || kind;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    // A failed blob request carries its JSON error as a Blob too.
    const body = e?.response?.data;
    if (body instanceof Blob) {
      try {
        e.response.data = JSON.parse(await body.text());
      } catch {
        // not JSON; fall back to the generic message
      }
    }
    toast.error(errorMessage(e, evidence ? 'Could not download the evidence.' : 'Could not download the attachment.'));
  } finally {
    downloading.value = null;
  }
}

// --- Editing a resolved report --------------------------------------------
const removeAttachment = shallowRef(false);
function startEdit() {
  const r = viewing.value;
  form.value = {
    ...form.value,
    comments: (status.value === 3 ? r.adviser_notes : r.prefect_notes) ?? '',
    file: null,
  };
  removeAttachment.value = false;
  mode.value = 'edit';
}
function saveNotes() {
  if (!hasNote()) {
    toast.error('Write how it was resolved first.');
    return;
  }
  const file = chosenFile();
  const problem = attachmentProblem(file);
  if (problem) {
    toast.error(problem);
    return;
  }
  let body = { comments: form.value.comments, removeAttachment: removeAttachment.value };
  if (file) {
    body = new FormData();
    body.append('comments', form.value.comments);
    body.append('file', file);
  }
  act({ method: 'patch', action: 'notes' }, body, 'Notes updated.');
}

// The adviser endorses the report to the Prefect of Discipline when it
// needs further intervention, with the reason.
function endorse() {
  if (!hasNote()) {
    toast.error('Write why it needs the Prefect of Discipline first.');
    return;
  }
  if (chosenFile()) {
    toast.error('An attachment goes with a resolution. Remove the file to endorse.');
    return;
  }
  act({ method: 'patch', action: 'escalate' }, { comments: form.value.comments }, 'Endorsed to the Prefect of Discipline.');
}
function requestMeeting() {
  if (!form.value.date || !form.value.time) {
    toast.error('Choose the date and time.');
    return;
  }
  if (counseling.value ? !counseledIds.value.size : !inviteCount.value) {
    toast.error(counseling.value ? 'Choose at least one student.' : 'Choose at least one parent.');
    return;
  }
  const { date, time, note, channel } = form.value;
  if (counseling.value) {
    act(
      { method: 'post', action: 'meeting' },
      { type: 'counseling', date, time, note, studentIds: [...counseledIds.value] },
      'Counseling scheduled. The adviser has been told.',
    );
    return;
  }
  sendMeeting({ type: meetingType.value, date, time, note, channel, parentIds: [...invitedIds.value] });
}
// The server's message says how it was sent, and names any parent the email
// didn't reach (a warning, since the request itself went out).
async function sendMeeting(body) {
  let result = null;
  const ok = await run(
    async () => (result = await apiRequest('post', `/parent-records/reports/${viewing.value.reportID}/meeting`, body)),
    { error: 'Could not send the meeting request.' },
  );
  if (!ok) return;
  if (result?.notEmailed?.length) toast.warning(result.msg);
  else toast.success(result?.msg || 'Meeting request sent.');
  viewOpen.value = false;
  load();
}
</script>

<template>
  <UiTableCard
    v-model:tab="tab"
    title="Discipline records"
    :description="
      isAdviser
        ? 'Reports you filed or about your advisory class. Settle those about your advisory students, or endorse them to the Prefect of Discipline.'
        : isTeacher
          ? 'Reports you filed. The student\'s class adviser settles them or endorses them to the Prefect of Discipline.'
          : 'Reports endorsed to you, cases you recorded, and meetings with parents.'
    "
    :tabs="TABS"
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error"
    search-placeholder="Search reports"
    :empty-text="tab === 1 ? 'Nothing for resolution.' : 'No reports here.'"
    row-label="name"
    :add-label="isPrefect ? 'Add record' : ''"
    :row-actions="(row) => [{ title: 'View details', icon: 'mdi-file-document-outline', onClick: () => view(row) }]"
    @add="recordOpen = true"
  >
    <template #[`item.name`]="{ item }">
      <UiPerson :name="item.name" :subtitle="[item.lrnNo && `LRN ${item.lrnNo}`, item.room_section].filter(Boolean).join(' · ')" />
    </template>
    <template #[`item.report_type`]="{ item }">
      <UiBadge :variant="reportType(item.report_type).variant">{{ reportType(item.report_type).name }}</UiBadge>
    </template>
    <template #[`item.created_at`]="{ item }">{{ formatDate(item.created_at) }}</template>
    <template #[`item.severity`]="{ item }">
      <UiBadge v-if="reportSeverity(item.severity)" :variant="reportSeverity(item.severity).variant">
        {{ reportSeverity(item.severity).name }}
      </UiBadge>
      <span v-else class="text-medium-emphasis">Not set</span>
    </template>
    <template #[`item.status`]="{ item }">
      <UiBadge :variant="stageOf(item).variant">{{ stageOf(item).text }}</UiBadge>
    </template>
    <template #[`item.teacher_name`]="{ item }">
      <span>{{ item.teacher_name }}</span>
      <UiBadge v-if="item.source === 'prefect'" variant="violet" class="ml-2">Prefect</UiBadge>
    </template>
    <template #[`item.compliance`]="{ item }">
      <UiBadge v-if="complianceStatus(item.compliance)" :variant="complianceStatus(item.compliance).variant">
        {{ complianceStatus(item.compliance).title }}
      </UiBadge>
      <span v-else class="text-medium-emphasis">—</span>
    </template>
    <template #[`item.meeting_schedule`]="{ item }">
      <span>{{ meetingWhen(item) || '—' }}</span>
      <UiBadge v-if="Number(item.status) === 5" variant="secondary" class="ml-2">{{ meetingTypeOf(item).name }}</UiBadge>
      <UiBadge v-if="meetingOf(item)" :variant="meetingOf(item).variant" class="ml-2">{{ meetingOf(item).text }}</UiBadge>
    </template>
  </UiTableCard>

  <UiDialog
    v-model="viewOpen"
    :title="viewing?.name ?? 'Report'"
    :description="viewing ? `${reportType(viewing.report_type).name} concern · ${viewing.grade_level}${viewing.room_section ? ` · ${viewing.room_section}` : ''}` : ''"
    size="lg"
  >
    <div v-if="viewing" class="form-stack">
      <UiSteps :steps="progress.steps" :current="progress.current" />
      <v-alert type="info" variant="tonal" density="compact" icon="mdi-information-outline">{{ nextStep }}</v-alert>

      <!-- The case, and what this person can do with it -->
      <template v-if="!mode">
        <div class="d-flex flex-wrap ga-2">
          <UiBadge :variant="stageOf(viewing).variant">{{ stageOf(viewing).text }}</UiBadge>
          <UiBadge v-if="meetingOf(viewing)" :variant="meetingOf(viewing).variant">{{ meetingOf(viewing).text }}</UiBadge>
          <UiBadge v-if="!isPrefect && reportSeverity(viewing.severity)" :variant="reportSeverity(viewing.severity).variant">
            {{ reportSeverity(viewing.severity).name }} case
          </UiBadge>
          <UiBadge v-if="viewing.source === 'prefect'" variant="violet">Recorded by the Prefect of Discipline</UiBadge>
          <UiBadge v-if="complianceStatus(viewing.compliance)" :variant="complianceStatus(viewing.compliance).variant">
            Compliance: {{ complianceStatus(viewing.compliance).title }}
          </UiBadge>
        </div>

        <section v-if="choices.length" class="case-section" aria-label="What would you like to do?">
          <p class="case-section__title">What would you like to do?</p>
          <UiChoiceCards :items="choices" @select="choose" />
        </section>

      <div v-if="isPrefect" class="severity">
        <div class="severity__head">
          <span class="text-body-2 text-medium-emphasis">Severity</span>
          <v-menu location="bottom start" :close-on-content-click="false" max-width="380">
            <template #activator="{ props: menu }">
              <v-btn v-bind="menu" icon="mdi-help-circle-outline" size="x-small" variant="text" aria-label="What the severity levels mean" />
            </template>
            <v-card class="severity__help">
              <p class="text-subtitle-2 mb-1">What the levels mean</p>
              <p class="text-body-2 text-medium-emphasis mb-3">
                Classify the case by what happened, not how far it has gone. It sets the behavior summary on your dashboard.
              </p>
              <div v-for="level in REPORT_SEVERITIES" :key="level.id" class="severity__level">
                <UiBadge :variant="level.variant">{{ level.name }}</UiBadge>
                <p class="text-body-2 mb-1 mt-1">{{ level.description }}</p>
                <p class="text-caption text-medium-emphasis mb-0">e.g. {{ level.examples }}</p>
              </div>
            </v-card>
          </v-menu>
        </div>
        <v-btn-toggle
          :model-value="viewing.severity ?? null"
          :disabled="savingSeverity"
          density="compact"
          variant="outlined"
          divided
          @update:model-value="setSeverity"
        >
          <v-btn v-for="level in REPORT_SEVERITIES" :key="level.id" :value="level.id" size="small">{{ level.name }}</v-btn>
        </v-btn-toggle>
        <p v-if="!viewing.severity" class="text-caption text-medium-emphasis mb-0">Not classified yet.</p>
      </div>

        <section class="case-section" aria-label="About the report">
          <p class="case-section__title">About the report</p>
          <UiDetailList :items="details" />
        </section>

        <section class="case-section" aria-label="Files and students involved">
          <p class="case-section__title">Files and students involved</p>
          <div v-if="viewing.evidence_file_name">
            <p class="text-body-2 text-medium-emphasis mb-2">Evidence from the report</p>
            <v-btn
              variant="outlined"
              size="small"
              prepend-icon="mdi-file-image-outline"
              :loading="downloading === 'evidence'"
              @click="downloadAttachment('evidence')"
            >
              {{ viewing.evidence_file_name }}
            </v-btn>
          </div>
          <div v-if="viewing.resolution_file_name">
            <p class="text-body-2 text-medium-emphasis mb-2">Resolution attachment</p>
            <v-btn
              variant="outlined"
              size="small"
              prepend-icon="mdi-paperclip"
              :loading="downloading === 'resolution'"
              @click="downloadAttachment('resolution')"
            >
              {{ viewing.resolution_file_name }}
            </v-btn>
          </div>
          <div>
            <p class="text-body-2 text-medium-emphasis mb-2">Other students involved</p>
            <UiBadgeList :items="viewing.tagged || []" empty="No other students tagged." />
          </div>
        </section>

        <section v-if="noteDetails.length" class="case-section" aria-label="Notes">
          <p class="case-section__title">Notes</p>
          <UiDetailList :items="noteDetails" />
        </section>

        <section v-if="meetingDetails.length" class="case-section" :aria-label="isCounseling(viewing) ? 'Counseling' : 'Meeting'">
          <p class="case-section__title">{{ isCounseling(viewing) ? 'Counseling' : 'Meeting' }}</p>
          <UiDetailList :items="meetingDetails" />
        </section>

        <section v-if="actionDetails.length" class="case-section" aria-label="Disciplinary action">
          <p class="case-section__title">Disciplinary action</p>
          <UiDetailList :items="actionDetails" />
        </section>
      </template>

      <!-- One task at a time -->
      <template v-else>
        <div class="task-head">
          <p class="task-head__title">{{ MODE_TITLES[mode] }}</p>
          <p class="task-head__hint">{{ MODE_HINTS[mode] }}</p>
        </div>

        <!-- Disciplinary action and compliance -->
        <template v-if="mode === 'action'">
          <fieldset v-if="isDisciplinary" class="action-group">
            <legend class="text-body-2 mb-1">Assigned action <span class="text-error">*</span></legend>
            <v-radio-group
              :model-value="actionForm.disciplinary_action_id"
              density="compact"
              hide-details
              @update:model-value="actionForm = { ...actionForm, disciplinary_action_id: $event }"
            >
              <v-radio v-for="a in actionChoices" :key="a.id" :label="a.archived ? `${a.name} (archived)` : a.name" :value="Number(a.id)" />
            </v-radio-group>
            <p v-if="!actionChoices.length" class="text-body-2 text-medium-emphasis mb-0">
              No disciplinary actions yet. Add them on the Disciplinary Actions page.
            </p>
          </fieldset>
          <div class="form-grid">
            <UiFormFields v-model:values="actionForm" :fields="complianceField" />
          </div>
          <p class="text-body-2 text-medium-emphasis mb-0">
            The compliance shows <strong>Overdue</strong> by itself once the due date passes without it being Completed.
          </p>
          <p v-if="savedCompliance?.value === 'overdue'" class="text-body-2 text-error mb-0">
            The due date has passed and the action isn't completed yet.
          </p>
        </template>

        <!-- Resolve, endorse, or edit the resolution note -->
        <template v-else-if="mode === 'resolve' || mode === 'endorse' || mode === 'edit'">
          <fieldset v-if="mode === 'resolve' && canActAsPrefect && isDisciplinary" class="action-group">
            <legend class="text-body-2 mb-1">Disciplinary action <span class="text-error">*</span></legend>
            <v-radio-group
              :model-value="actionForm.disciplinary_action_id"
              density="compact"
              hide-details
              @update:model-value="actionForm = { ...actionForm, disciplinary_action_id: $event }"
            >
              <v-radio v-for="a in actionChoices" :key="a.id" :label="a.archived ? `${a.name} (archived)` : a.name" :value="Number(a.id)" />
            </v-radio-group>
          </fieldset>
          <UiFormFields v-model:values="form" :fields="noteFields" />
          <v-checkbox
            v-if="editing && viewing.resolution_file_name && !chosenFile()"
            v-model="removeAttachment"
            :label="`Remove ${viewing.resolution_file_name}`"
            density="compact"
            hide-details
          />
          <v-switch
            v-if="mode === 'resolve' && canActAsPrefect"
            v-model="notifyParents"
            color="primary"
            label="Tell the parents how it was resolved"
            inset
            density="compact"
            hide-details
          />
        </template>

        <!-- Conference or counseling -->
        <template v-else-if="mode === 'meeting'">
          <UiOptionList v-model="meetingType" :items="MEETING_TYPES" item-subtitle="description" />
          <p class="text-body-2 text-medium-emphasis mb-0">
            {{
              counseling
                ? 'Choose the students to call in. Their class advisers are told.'
                : 'The parents of everyone on this report are asked. Each can accept, decline, or move it to another time.'
            }}
          </p>

          <div class="invitees">
            <div v-if="loadingInvitees" class="invitees__empty">{{ counseling ? 'Loading students…' : 'Loading parents…' }}</div>
            <template v-else-if="counseling">
              <label
                v-for="s in invitees"
                :key="s.studentID"
                class="parent invitee__pick"
                :class="{ 'parent--on': counseledIds.has(s.studentID) }"
              >
                <input
                  :id="`counsel-${s.studentID}`"
                  type="checkbox"
                  class="parent__check"
                  :checked="counseledIds.has(s.studentID)"
                  @change="toggleCounseled(s.studentID)"
                />
                <span class="parent__body">
                  <span class="parent__name">{{ titleCase(s.studentName) }}</span>
                  <span class="parent__contact">{{ [s.gradeLevel, s.section, s.lrnNo && `LRN ${s.lrnNo}`].filter(Boolean).join(' · ') }}</span>
                </span>
                <UiBadge :variant="s.reported ? 'amber' : 'secondary'" class="ml-auto">{{ s.reported ? 'Reported' : 'Involved' }}</UiBadge>
              </label>
            </template>
            <section v-for="s in invitees" v-else :key="s.studentID" class="invitee" :aria-label="titleCase(s.studentName)">
              <div class="invitee__student">
                <div class="invitee__student-text">
                  <span class="invitee__name">{{ titleCase(s.studentName) }}</span>
                  <span class="invitee__meta">{{ [s.gradeLevel, s.section, s.lrnNo && `LRN ${s.lrnNo}`].filter(Boolean).join(' · ') }}</span>
                </div>
                <UiBadge :variant="s.reported ? 'amber' : 'secondary'">{{ s.reported ? 'Reported' : 'Involved' }}</UiBadge>
              </div>

              <template v-if="s.parents.length">
                <p class="invitee__count">
                  {{ chosenFor(s) }} of {{ s.parents.length }} parent{{ s.parents.length === 1 ? '' : 's' }} will be asked
                </p>
                <label
                  v-for="p in s.parents"
                  :key="`${s.studentID}-${p.id}`"
                  class="parent"
                  :class="{ 'parent--on': invitedIds.has(p.id) }"
                >
                  <input
                    :id="`invite-${s.studentID}-${p.id}`"
                    type="checkbox"
                    class="parent__check"
                    :checked="invitedIds.has(p.id)"
                    @change="toggleInvite(p.id)"
                  />
                  <UiAvatar :name="titleCase(p.name)" :src="parentPhoto(p)" :size="36" />
                  <span class="parent__body">
                    <span class="parent__name">{{ titleCase(p.name) }}</span>
                    <span v-if="parentContacts(p).length" class="parent__contacts">
                      <span v-for="c in parentContacts(p)" :key="c.icon" class="parent__contact">
                        <v-icon size="14">{{ c.icon }}</v-icon>{{ c.text }}
                      </span>
                    </span>
                    <span v-else class="parent__contact parent__contact--none">No contact details on their account</span>
                  </span>
                </label>
              </template>

              <p v-else class="invitee__none">
                <v-icon size="16">mdi-account-off-outline</v-icon>
                No parent account linked, so this family can't be asked here.
              </p>
            </section>
          </div>

          <div class="form-grid">
            <UiFormFields v-model:values="form" :fields="meetingFields" />
          </div>
          <v-btn
            v-if="form.note !== noteTemplate"
            variant="text"
            size="small"
            prepend-icon="mdi-restore"
            class="align-self-start"
            @click="form = { ...form, note: noteTemplate }"
          >
            Use the template
          </v-btn>
        </template>
      </template>
    </div>

    <template #footer="{ close }">
      <template v-if="!mode">
        <v-btn variant="outlined" class="mr-auto" @click="close">Close</v-btn>
        <v-btn
          v-if="canActAsPrefect && meetingWhen(viewing)"
          variant="text"
          prepend-icon="mdi-calendar-month-outline"
          @click="router.push(router.currentRoute.value.path.replace(/[^/]+$/, 'appointments'))"
        >
          Open in Appointments
        </v-btn>
      </template>
      <template v-else>
        <v-btn variant="outlined" class="mr-auto" prepend-icon="mdi-arrow-left" :disabled="busy" @click="backToCase">Back</v-btn>
        <v-btn v-if="mode === 'resolve'" color="primary" prepend-icon="mdi-check" :loading="busy" @click="resolve">Resolve</v-btn>
        <v-btn v-else-if="mode === 'endorse'" color="primary" prepend-icon="mdi-arrow-up-bold-outline" :loading="busy" @click="endorse">
          Endorse
        </v-btn>
        <v-btn v-else-if="mode === 'edit'" color="primary" :loading="busy" @click="saveNotes">Save</v-btn>
        <v-btn
          v-else-if="mode === 'action'"
          color="primary"
          prepend-icon="mdi-content-save-outline"
          :loading="savingAction"
          :disabled="!actionDirty"
          @click="saveAction()"
        >
          Save
        </v-btn>
        <v-btn
          v-else-if="mode === 'meeting' && counseling"
          color="primary"
          prepend-icon="mdi-calendar-check-outline"
          :loading="busy"
          :disabled="!counseledIds.size"
          @click="requestMeeting"
        >
          Schedule counseling
        </v-btn>
        <v-btn
          v-else-if="mode === 'meeting'"
          color="primary"
          prepend-icon="mdi-send-outline"
          :loading="busy"
          :disabled="!inviteCount"
          @click="requestMeeting"
        >
          Send to {{ inviteCount }} parent{{ inviteCount === 1 ? '' : 's' }}
        </v-btn>
      </template>
    </template>
  </UiDialog>

  <StudentDisciplinaryDialog v-if="isPrefect" v-model="recordOpen" mode="prefect" @saved="recorded" />
</template>

<style scoped>
.action-group {
  border: 0;
  margin: 0;
  padding: 0;
}
.case-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
}
.case-section__title,
.task-head__title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}
.task-head {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.task-head__hint {
  margin: 0;
  font-size: 14px;
  color: var(--muted-foreground);
}
.severity {
  display: grid;
  gap: 6px;
  justify-items: start;
}
.severity__head {
  display: flex;
  align-items: center;
  gap: 2px;
}
.severity__help {
  padding: 16px;
}
.severity__level + .severity__level {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--border);
}
.invitees {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--card);
}
.invitees__empty {
  padding: 12px;
  font-size: 13px;
  color: var(--muted-foreground);
}
.invitee {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
}
.invitee + .invitee {
  border-top: 1px solid var(--border);
}
.invitee__student {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.invitee__student-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.invitee__name {
  font-size: 14px;
  font-weight: 600;
}
.invitee__meta,
.invitee__count,
.invitee__none {
  font-size: 13px;
  color: var(--muted-foreground);
}
.invitee__count {
  margin: 0;
}
.invitee__pick {
  margin: 12px;
}
.invitee__pick + .invitee__pick {
  margin-top: 0;
}

/* One linked parent: a selectable card with their contact details */
.parent {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--card);
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.parent:hover {
  border-color: var(--border-strong);
}
.parent--on {
  border-color: var(--foreground);
  background: var(--subtle);
}
.parent:focus-within {
  box-shadow: 0 0 0 3px var(--ring-halo);
}
.parent__check {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  accent-color: var(--foreground);
}
.parent__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.parent__name {
  font-size: 14px;
  font-weight: 500;
}
.parent__contacts {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 12px;
}
.parent__contact {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-size: 13px;
  color: var(--muted-foreground);
  overflow-wrap: anywhere;
}
.parent__contact--none {
  font-style: italic;
}

/* No parent account */
.invitee__none {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin: 0;
}
</style>
